import { useAuthenticator } from "@aws-amplify/ui-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { consumeOAuthRedirectState } from "../../auth/oauthRedirectState";
import {
  ensurePersonaTokenClaim,
  mapProvisionedPersonaToShellPersona,
  provisionActiveSessionPersona,
  type PersonaProvisioningResult,
  type PersonaTokenClaimResult,
} from "../../auth/personaProvisioningRuntime";
import {
  describeClaimRefreshFailure,
  NO_PERSONA_INTENT_FOR_FEDERATED_USER,
  normalizePersonaProvisioningFailureReason,
  resolvePersonaProvisioningCompletion,
} from "../../auth/personaProvisioningOutcome";
import { useAppDispatch, useAppSelector } from "../../state/hooks";
import { useShellProfile } from "../../state/useShellProfile";
import {
  setDefaultModule,
  setPersonaOverride,
  resetShellState,
} from "../../state/shellSlice";
import { clearShellStorage } from "../../state/shellStorage";
import {
  PUBLIC_AUTH_CONFIG,
  isNonProductionAuthSurface,
  resolveGrantedPostAuthPersonaChoices,
  resolvePostAuthPersonaSelectionMode,
  type PostAuthPersonaSelectionMode,
} from "../../config/publicAuth";
import {
  AuthPage as PublicAuthPage,
  type PublicAuthMode,
} from "../../components/public";
import type { AccountType } from "@triedge/react-components";
import {
  isModuleId,
  isSafeInternalPath,
  resolvePostAuthTarget,
} from "../routePolicy";
import {
  clearLocalAuthState,
  resolveLogoutTransitionTarget,
} from "../../auth/logoutTransition";
import {
  AuthFailureScreen,
  AuthTransitionScreen,
} from "./AuthTransitionScreen";
import {
  resolveAuthGateRenderState,
  resolveAuthRouteRenderState,
} from "./authRenderState";
import { PublicFinancialAidEntryRoute } from "./PublicFinancialAidEntryRoute";
import { isPublicFinancialAidEntryPath } from "../../financial-aid/publicFinancialAidEntry";
import { useCompleteAssistantSpreadsheetBridgeAuthMutation } from "../../state/domainRouterApi";
import {
  ASSISTANT_SPREADSHEET_BRIDGE_SETTINGS_ROUTE,
  clearAssistantSpreadsheetBridgeReturnContext,
  getAssistantSpreadsheetBridgeReturnContext,
  readAssistantSpreadsheetBridgeCallbackQuery,
  storeAssistantSpreadsheetBridgeCompletionContext,
} from "../../components/ai/assistantSpreadsheetBridgeClientState";
import { WorkspaceTransitionGate } from "../../workspace-preload/WorkspaceTransitionGate";

const ENABLE_GOOGLE_SOCIAL = PUBLIC_AUTH_CONFIG.googleSocialEnabled;

const isOverridePersona = (
  value: string | null | undefined,
): value is "parent" | "staff" | "administrator" =>
  value === "parent" || value === "staff" || value === "administrator";

const normalizeAccountType = (
  value: string | null | undefined,
): AccountType | null =>
  value === "staff" || value === "parent" ? value : null;

const normalizeQueryText = (
  value: string | null | undefined,
): string | null => {
  const trimmed = String(value ?? "").trim();
  return trimmed.length > 0 ? trimmed : null;
};

const readPathname = (value: string | null): string | null => {
  if (!value) {
    return null;
  }
  try {
    return new URL(value, "https://ystnj.local").pathname;
  } catch {
    return value.split("?")[0] || null;
  }
};

const isParentLaneReturnTo = (value: string | null): boolean => {
  const pathname = readPathname(value);
  return Boolean(
    pathname === "/apply" ||
      pathname?.startsWith("/parent/") ||
      pathname?.startsWith("/school/parent/"),
  );
};

const isFinancialAidInviteReturnTo = (value: string | null): boolean => {
  if (!value) {
    return false;
  }
  try {
    const parsed = new URL(value, "https://ystnj.local");
    const pathname = parsed.pathname.toLowerCase();
    return (
      isParentLaneReturnTo(value) &&
      (pathname.includes("financial-aid") ||
        pathname.includes("financialaid")) &&
      Boolean(parsed.searchParams.get("invite")?.trim())
    );
  } catch {
    return false;
  }
};

const readText = (value: unknown, fallback = "Unavailable"): string => {
  const trimmed = String(value ?? "").trim();
  return trimmed.length > 0 ? trimmed : fallback;
};

const readBooleanText = (value: boolean | null | undefined): string => {
  if (value === true) {
    return "yes";
  }
  if (value === false) {
    return "no";
  }
  return "Unavailable";
};

const readListText = (value: readonly string[] | null | undefined): string => {
  const entries = (value ?? []).map((entry) => entry.trim()).filter(Boolean);
  return entries.length > 0 ? entries.join(", ") : "Unavailable";
};

const AuthDiagnosticRow = ({
  label,
  value,
}: {
  label: string;
  value: string;
}) => (
  <>
    <dt className="font-semibold text-irongrey">{label}</dt>
    <dd className="break-all text-irongrey/70">{value}</dd>
  </>
);

type PostAuthPersonaChoice = "parent" | "staff" | "administrator";

type PostAuthPersonaSelectionFailure = {
  reason: string;
  message: string;
  selection: string;
  backendResult: PersonaProvisioningResult | null;
  tokenClaim: PersonaTokenClaimResult | null;
  error: unknown;
};

const POST_AUTH_PERSONA_CHOICES: Array<{
  value: PostAuthPersonaChoice;
  label: string;
}> = [
  { value: "staff", label: "Staff" },
  { value: "administrator", label: "Administrator" },
  { value: "parent", label: "Parent" },
];

const USABLE_COGNITO_ROLE_GROUPS = new Set([
  "parent",
  "staff",
  "editor",
  "nurse",
  "admin",
  "administrator",
  "owner",
  "super-administrator",
]);

const normalizeGroupToken = (value: string): string =>
  value.trim().toLowerCase();

const hasUsableCognitoRoleGroup = (
  groups: readonly string[] | null | undefined,
): boolean =>
  (groups ?? []).some((group) =>
    USABLE_COGNITO_ROLE_GROUPS.has(normalizeGroupToken(group)),
  );

const POST_AUTH_PERSONA_EQUIVALENTS: Record<PostAuthPersonaChoice, string[]> = {
  parent: ["parent"],
  staff: ["staff", "editor", "nurse"],
  administrator: ["administrator", "admin", "owner", "super-administrator"],
};

const provisionedPersonaMatchesSelection = (
  requestedPersona: PostAuthPersonaChoice,
  provisionedPersona: string,
): boolean => {
  const allowed = new Set(
    POST_AUTH_PERSONA_EQUIVALENTS[requestedPersona].map(normalizeGroupToken),
  );
  return allowed.has(normalizeGroupToken(provisionedPersona));
};

const resolvePersonaMismatchReason = (
  requestedPersona: PostAuthPersonaChoice,
  backendReason: string | null | undefined,
): string => {
  const normalizedReason = normalizePersonaProvisioningFailureReason(
    backendReason,
    requestedPersona === "administrator"
      ? "super-administrator-not-allowlisted"
      : "no_matching_assignment_rule",
  );
  if (normalizedReason === "existing_persona_group") {
    return requestedPersona === "administrator"
      ? "super-administrator-not-allowlisted"
      : "no_matching_assignment_rule";
  }
  return normalizedReason;
};

const buildPostAuthFailure = ({
  reason,
  message,
  selection,
  backendResult = null,
  tokenClaim = null,
  error = null,
}: {
  reason: string;
  message: string;
  selection: string;
  backendResult?: PersonaProvisioningResult | null;
  tokenClaim?: PersonaTokenClaimResult | null;
  error?: unknown;
}): PostAuthPersonaSelectionFailure => ({
  reason: normalizePersonaProvisioningFailureReason(reason),
  message,
  selection,
  backendResult,
  tokenClaim,
  error,
});

export const PostGooglePersonaSelectionScreen = ({
  returnTo,
  defaultModule,
  schoolId,
  selectionMode = "open",
  testId = "post-google-persona-selection-screen",
}: {
  returnTo: string | null;
  defaultModule: string | null;
  schoolId?: string | null;
  selectionMode?: PostAuthPersonaSelectionMode;
  testId?: string;
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { signOut } = useAuthenticator((context) => [context.signOut]);
  const shellState = useAppSelector((state) => state.shell);
  const { effectivePersona, profile, personaTokenGroups } = useShellProfile();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [failure, setFailure] =
    useState<PostAuthPersonaSelectionFailure | null>(null);
  const groups = useMemo(
    () =>
      personaTokenGroups.length > 0
        ? personaTokenGroups
        : (profile?.groups ?? []),
    [personaTokenGroups, profile?.groups],
  );
  const grantedPersonaChoices = useMemo(
    () => resolveGrantedPostAuthPersonaChoices(groups),
    [groups],
  );
  const isGrantedGroupSelection = selectionMode === "granted-groups";
  const visiblePersonaChoices = useMemo(
    () =>
      isGrantedGroupSelection
        ? POST_AUTH_PERSONA_CHOICES.filter((choice) =>
            grantedPersonaChoices.includes(choice.value),
          )
        : POST_AUTH_PERSONA_CHOICES,
    [grantedPersonaChoices, isGrantedGroupSelection],
  );

  const completeSelection = (
    persona: string,
    authGroups: readonly string[] | null | undefined,
  ) => {
    if (isModuleId(defaultModule)) {
      dispatch(setDefaultModule(defaultModule));
    }
    const target = resolvePostAuthTarget({
      persona,
      lastVisitedPath: shellState.lastVisitedPath,
      defaultModule: isModuleId(defaultModule)
        ? defaultModule
        : shellState.defaultModule,
      returnTo,
      authGroups: authGroups ? [...authGroups] : profile?.groups,
    });
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("ystnj-auth-session-updated"));
    }
    navigate(target, { replace: true });
  };

  const handleGrantedPersonaSelection = (persona: PostAuthPersonaChoice) => {
    dispatch(setPersonaOverride(persona));
    completeSelection(persona, groups);
  };

  const handleUseCognitoRole = () => {
    if (!hasUsableCognitoRoleGroup(groups)) {
      setFailure(
        buildPostAuthFailure({
          reason: NO_PERSONA_INTENT_FOR_FEDERATED_USER,
          message:
            "Google sign-in completed, but this account is not mapped to a selectable persona or Cognito role.",
          selection: "Use Cognito role",
        }),
      );
      return;
    }
    completeSelection(effectivePersona, groups);
  };

  const handleReauthentication = async () => {
    try {
      await signOut();
    } catch (error) {
      console.warn(
        "[auth] signOut failed after authoritative staff role reconciliation.",
        error,
      );
    } finally {
      dispatch(resetShellState());
      clearShellStorage(profile?.userSub ?? null);
      clearLocalAuthState();
      if (typeof window !== "undefined") {
        window.location.assign("/auth?mode=signin");
      }
    }
  };

  const handleProvisionPersona = async (
    requestedPersona: PostAuthPersonaChoice,
  ) => {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    setFailure(null);
    try {
      const result = await provisionActiveSessionPersona({
        emailHint: profile?.email,
        requestedPersona,
        requestedAccountType:
          requestedPersona === "parent" || requestedPersona === "staff"
            ? requestedPersona
            : null,
        schoolId,
        source: "post-google-persona-selection",
      });
      const completion = resolvePersonaProvisioningCompletion(result, {
        hasPersonaIntent: true,
        isFederatedProvider: true,
      });
      if (completion.kind === "reauthentication_required") {
        setFailure(
          buildPostAuthFailure({
            reason: "staff-role-updated-reauthentication-required",
            message: completion.message,
            selection: requestedPersona,
            backendResult: result,
          }),
        );
        return;
      }
      if (completion.kind === "failure") {
        setFailure(
          buildPostAuthFailure({
            reason: completion.failureReason,
            message: completion.message,
            selection: requestedPersona,
            backendResult: result,
          }),
        );
        return;
      }
      if (
        !provisionedPersonaMatchesSelection(
          requestedPersona,
          completion.targetPersona,
        )
      ) {
        setFailure(
          buildPostAuthFailure({
            reason: resolvePersonaMismatchReason(
              requestedPersona,
              result?.reason,
            ),
            message: `The selected ${requestedPersona} persona could not be assigned to this account. Cognito returned ${completion.targetPersona}.`,
            selection: requestedPersona,
            backendResult: result,
          }),
        );
        return;
      }

      const tokenClaim = await ensurePersonaTokenClaim(
        completion.targetPersona,
        {
          maxAttempts: 6,
          baseDelayMs: 250,
        },
      );
      if (!tokenClaim.ready) {
        setFailure(
          buildPostAuthFailure({
            reason: tokenClaim.reason,
            message: describeClaimRefreshFailure(tokenClaim.reason),
            selection: requestedPersona,
            backendResult: result,
            tokenClaim,
          }),
        );
        return;
      }

      const shellPersona = mapProvisionedPersonaToShellPersona(
        completion.targetPersona,
      );
      completeSelection(shellPersona, tokenClaim.groups);
    } catch (error) {
      const message =
        error instanceof Error && error.message.trim().length > 0
          ? error.message
          : "AppSync failed while assigning the selected persona.";
      setFailure(
        buildPostAuthFailure({
          reason: "appsync_failure",
          message,
          selection: requestedPersona,
          error,
        }),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (failure) {
    const error = failure.error instanceof Error ? failure.error : null;
    const isReauthenticationFailure =
      failure.reason ===
      "staff-role-updated-reauthentication-required";
    const diagnostics = (
      <details
        data-testid={`${testId}-failure-diagnostics`}
        className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3"
        open
      >
        <summary className="cursor-pointer text-sm font-semibold text-irongrey">
          Persona selection diagnostics
        </summary>
        <dl className="mt-3 grid grid-cols-[minmax(0,180px)_1fr] gap-x-3 gap-y-2 text-xs">
          <AuthDiagnosticRow
            label="Failure reason"
            value={readText(failure.reason)}
          />
          <AuthDiagnosticRow
            label="Selection"
            value={readText(failure.selection)}
          />
          <AuthDiagnosticRow label="Email" value={readText(profile?.email)} />
          <AuthDiagnosticRow
            label="Token username"
            value={readText(profile?.username)}
          />
          <AuthDiagnosticRow
            label="Token groups"
            value={readListText(groups)}
          />
          <AuthDiagnosticRow
            label="Backend status"
            value={readText(failure.backendResult?.status)}
          />
          <AuthDiagnosticRow
            label="Backend persona"
            value={readText(failure.backendResult?.persona)}
          />
          <AuthDiagnosticRow
            label="Backend assigned group"
            value={readText(failure.backendResult?.assignedGroup)}
          />
          <AuthDiagnosticRow
            label="Backend pending approval"
            value={readBooleanText(failure.backendResult?.pendingApproval)}
          />
          <AuthDiagnosticRow
            label="Backend reason"
            value={readText(failure.backendResult?.reason)}
          />
          <AuthDiagnosticRow
            label="Token refresh target"
            value={readText(failure.tokenClaim?.targetPersona)}
          />
          <AuthDiagnosticRow
            label="Token refresh ready"
            value={readBooleanText(failure.tokenClaim?.ready)}
          />
          <AuthDiagnosticRow
            label="Token refresh attempts"
            value={readText(failure.tokenClaim?.attempts)}
          />
          <AuthDiagnosticRow
            label="Token refresh reason"
            value={readText(failure.tokenClaim?.reason)}
          />
          <AuthDiagnosticRow
            label="Operation error class"
            value={readText(
              error?.name ?? (failure.error ? typeof failure.error : null),
            )}
          />
          <AuthDiagnosticRow
            label="Operation error"
            value={readText(
              error?.message ?? (failure.error ? String(failure.error) : null),
            )}
          />
        </dl>
      </details>
    );
    return (
      <AuthFailureScreen
        testId={`${testId}-failure-screen`}
        title="Unable to assign persona"
        description={failure.message}
        actions={
          isReauthenticationFailure ? (
            <button
              type="button"
              data-testid={`${testId}-failure-sign-in-again`}
              className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary/90"
              onClick={() => void handleReauthentication()}
            >
              Sign in again
            </button>
          ) : (
            <>
              <button
                type="button"
                data-testid={`${testId}-failure-retry`}
                className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary/90"
                onClick={() => setFailure(null)}
              >
                Choose another persona
              </button>
              <button
                type="button"
                data-testid={`${testId}-failure-start-over`}
                className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-irongrey transition hover:bg-slate-50"
                onClick={() => {
                  if (typeof window !== "undefined") {
                    window.location.assign("/auth?mode=signin");
                  }
                }}
              >
                Start over
              </button>
            </>
          )
        }
        diagnostics={diagnostics}
      />
    );
  }

  return (
    <section
      data-testid={testId}
      className="min-h-screen bg-background-light px-6 py-16 text-irongrey"
    >
      <div className="mx-auto max-w-xl rounded-3xl border border-gray-100 bg-white px-6 py-8 shadow-sm">
        <div className="mb-6">
          <h1 className="text-xl font-bold">Choose workspace access</h1>
          <p className="mt-1 text-sm text-irongrey/60">
            Google sign-in is complete. Select the YSTNJ access path for this
            Cognito session.
          </p>
        </div>
        <div className="space-y-3">
          {!isGrantedGroupSelection ? (
            <button
              type="button"
              data-testid="post-google-persona-use-cognito-role"
              className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-left text-sm font-semibold text-irongrey transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={isSubmitting}
              onClick={handleUseCognitoRole}
            >
              Use Cognito role
            </button>
          ) : null}
          {visiblePersonaChoices.map((choice) => (
            <button
              key={choice.value}
              type="button"
              data-testid={`post-google-persona-option-${choice.value}`}
              className="w-full rounded-xl border border-primary/25 bg-primary/5 px-4 py-3 text-left text-sm font-semibold text-irongrey transition hover:bg-primary/10 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={isSubmitting}
              onClick={() => {
                if (isGrantedGroupSelection) {
                  handleGrantedPersonaSelection(choice.value);
                  return;
                }
                void handleProvisionPersona(choice.value);
              }}
            >
              {choice.label}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export const AuthFailureRouteScreen = ({
  testId = "auth-failure-screen",
  title = "Unable to finish sign in",
}: {
  testId?: string;
  title?: string;
}) => {
  const dispatch = useAppDispatch();
  const { signOut } = useAuthenticator((context) => [context.signOut]);
  const {
    profile,
    personaProvisioningError,
    personaProvisioningStatus,
    personaProvisioningDiagnostics,
    personaTokenGroups,
    expectedTuple,
    observedTokenTuple,
    failureReason,
  } = useShellProfile();
  const diagnosticFailureReason =
    personaProvisioningStatus === "failed"
      ? readText(failureReason, "persona-provisioning-reason-missing")
      : readText(failureReason);
  const isStaffPendingApproval =
    diagnosticFailureReason === "staff_signup_pending_approval" ||
    personaProvisioningDiagnostics?.backendPendingApproval === true;
  const isReauthenticationRequired =
    diagnosticFailureReason ===
    "staff-role-updated-reauthentication-required";

  const resetClientState = () => {
    dispatch(resetShellState());
    clearShellStorage(profile?.userSub ?? null);
    clearLocalAuthState();
  };

  const handleRetrySignIn = () => {
    resetClientState();
    if (typeof window !== "undefined") {
      window.location.assign("/auth?mode=signin");
    }
  };

  const handleStartOver = async () => {
    try {
      await signOut();
    } catch (error) {
      console.warn(
        "[auth] signOut failed while restarting authentication.",
        error,
      );
    } finally {
      resetClientState();
      if (typeof window !== "undefined") {
        window.location.assign("/auth");
      }
    }
  };

  const handleReauthentication = async () => {
    try {
      await signOut();
    } catch (error) {
      console.warn(
        "[auth] signOut failed after authoritative staff role reconciliation.",
        error,
      );
    } finally {
      resetClientState();
      if (typeof window !== "undefined") {
        window.location.assign("/auth?mode=signin");
      }
    }
  };

  const showDiagnostics = isNonProductionAuthSurface({
    origin: expectedTuple.origin,
  });
  const diagnostics = showDiagnostics ? (
    <details
      data-testid={`${testId}-diagnostics`}
      className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3"
    >
      <summary className="cursor-pointer text-sm font-semibold text-irongrey">
        Auth diagnostics
      </summary>
      <dl className="mt-3 grid grid-cols-[minmax(0,180px)_1fr] gap-x-3 gap-y-2 text-xs">
        <AuthDiagnosticRow
          label="Current origin"
          value={readText(expectedTuple.origin)}
        />
        <AuthDiagnosticRow
          label="Origin allowlisted"
          value={expectedTuple.originSupported ? "yes" : "no"}
        />
        <AuthDiagnosticRow
          label="Expected user pool"
          value={readText(expectedTuple.userPoolId)}
        />
        <AuthDiagnosticRow
          label="Expected client id"
          value={readText(expectedTuple.userPoolClientId)}
        />
        <AuthDiagnosticRow
          label="Expected identity pool"
          value={readText(expectedTuple.identityPoolId)}
        />
        <AuthDiagnosticRow
          label="Expected OAuth domain"
          value={readText(expectedTuple.oauthDomain)}
        />
        <AuthDiagnosticRow
          label="Observed user pool"
          value={readText(observedTokenTuple?.userPoolId)}
        />
        <AuthDiagnosticRow
          label="Observed client id"
          value={readText(observedTokenTuple?.userPoolClientId)}
        />
        <AuthDiagnosticRow
          label="Active token key"
          value={readText(observedTokenTuple?.tokenKey)}
        />
        <AuthDiagnosticRow
          label="Authority matched"
          value={observedTokenTuple?.authorityMatched ? "yes" : "no"}
        />
        <AuthDiagnosticRow
          label="Failure reason"
          value={diagnosticFailureReason}
        />
        <AuthDiagnosticRow
          label="Provisioning status"
          value={readText(personaProvisioningStatus)}
        />
        <AuthDiagnosticRow
          label="Provider"
          value={readText(
            personaProvisioningDiagnostics?.provider ?? profile?.provider,
          )}
        />
        <AuthDiagnosticRow
          label="Email"
          value={readText(
            personaProvisioningDiagnostics?.email ?? profile?.email,
          )}
        />
        <AuthDiagnosticRow
          label="Token username"
          value={readText(
            personaProvisioningDiagnostics?.username ??
              profile?.username ??
              observedTokenTuple?.username,
          )}
        />
        <AuthDiagnosticRow
          label="Token role"
          value={readText(
            personaProvisioningDiagnostics?.role ?? profile?.role,
          )}
        />
        <AuthDiagnosticRow
          label="Token permissions"
          value={readListText(
            personaProvisioningDiagnostics?.permissions ?? profile?.permissions,
          )}
        />
        <AuthDiagnosticRow
          label="Token groups"
          value={readListText(
            personaProvisioningDiagnostics?.tokenGroups.length
              ? personaProvisioningDiagnostics.tokenGroups
              : personaTokenGroups.length
                ? personaTokenGroups
                : profile?.groups,
          )}
        />
        <AuthDiagnosticRow
          label="Auto upgrade candidate"
          value={readBooleanText(
            personaProvisioningDiagnostics?.autoUpgradeCandidate,
          )}
        />
        <AuthDiagnosticRow
          label="Auto upgrade decision"
          value={readText(personaProvisioningDiagnostics?.autoUpgradeDecision)}
        />
        <AuthDiagnosticRow
          label="Requested persona"
          value={readText(personaProvisioningDiagnostics?.requestedPersona)}
        />
        <AuthDiagnosticRow
          label="Requested account type"
          value={readText(personaProvisioningDiagnostics?.requestedAccountType)}
        />
        <AuthDiagnosticRow
          label="Intent email"
          value={readText(personaProvisioningDiagnostics?.intentEmail)}
        />
        <AuthDiagnosticRow
          label="Intent created"
          value={readText(personaProvisioningDiagnostics?.intentCreatedAt)}
        />
        <AuthDiagnosticRow
          label="Staff code present"
          value={readBooleanText(
            personaProvisioningDiagnostics?.staffSignupCodePresent,
          )}
        />
        <AuthDiagnosticRow
          label="School id"
          value={readText(personaProvisioningDiagnostics?.schoolId)}
        />
        <AuthDiagnosticRow
          label="Provision operation"
          value={readText(personaProvisioningDiagnostics?.operation)}
        />
        <AuthDiagnosticRow
          label="Operation status"
          value={readText(personaProvisioningDiagnostics?.operationStatus)}
        />
        <AuthDiagnosticRow
          label="Operation error class"
          value={readText(personaProvisioningDiagnostics?.operationErrorClass)}
        />
        <AuthDiagnosticRow
          label="Operation error"
          value={readText(
            personaProvisioningDiagnostics?.operationErrorMessage,
          )}
        />
        <AuthDiagnosticRow
          label="Backend status"
          value={readText(personaProvisioningDiagnostics?.backendStatus)}
        />
        <AuthDiagnosticRow
          label="Backend persona"
          value={readText(personaProvisioningDiagnostics?.backendPersona)}
        />
        <AuthDiagnosticRow
          label="Backend assigned group"
          value={readText(personaProvisioningDiagnostics?.backendAssignedGroup)}
        />
        <AuthDiagnosticRow
          label="Backend pending approval"
          value={readBooleanText(
            personaProvisioningDiagnostics?.backendPendingApproval,
          )}
        />
        <AuthDiagnosticRow
          label="Backend reason"
          value={readText(personaProvisioningDiagnostics?.backendReason)}
        />
        <AuthDiagnosticRow
          label="Backend payload type"
          value={readText(
            personaProvisioningDiagnostics?.backendObservedPayloadType,
          )}
        />
        <AuthDiagnosticRow
          label="Backend payload keys"
          value={readListText(
            personaProvisioningDiagnostics?.backendObservedPayloadKeys,
          )}
        />
        <AuthDiagnosticRow
          label="Token refresh target"
          value={readText(
            personaProvisioningDiagnostics?.tokenRefreshTargetPersona,
          )}
        />
        <AuthDiagnosticRow
          label="Token refresh ready"
          value={readBooleanText(
            personaProvisioningDiagnostics?.tokenRefreshReady,
          )}
        />
        <AuthDiagnosticRow
          label="Token refresh attempts"
          value={readText(personaProvisioningDiagnostics?.tokenRefreshAttempts)}
        />
        <AuthDiagnosticRow
          label="Token refresh reason"
          value={readText(personaProvisioningDiagnostics?.tokenRefreshReason)}
        />
      </dl>
    </details>
  ) : null;

  return (
    <AuthFailureScreen
      testId={testId}
      title={isStaffPendingApproval ? "Staff access pending" : title}
      description={
        isStaffPendingApproval
          ? "Your email is verified, but staff access has not been approved yet. Staff access requires an active staff signup code, an approved school email domain, or approval from a school administrator."
          : (personaProvisioningError ??
            "This session authenticated with Cognito, but YSTNJ could not finish preparing your access.")
      }
      actions={
        isReauthenticationRequired ? (
          <button
            type="button"
            data-testid={`${testId}-sign-in-again`}
            className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary/90"
            onClick={() => {
              void handleReauthentication();
            }}
          >
            Sign in again
          </button>
        ) : (
          <>
            <button
              type="button"
              data-testid={`${testId}-retry`}
              className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary/90"
              onClick={handleRetrySignIn}
            >
              Retry sign in
            </button>
            <button
              type="button"
              data-testid={`${testId}-start-over`}
              className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-irongrey transition hover:bg-slate-50"
              onClick={() => {
                void handleStartOver();
              }}
            >
              Start over
            </button>
          </>
        )
      }
      diagnostics={diagnostics}
    />
  );
};

const AssistantSpreadsheetBridgeCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const callbackQuery = useMemo(
    () => readAssistantSpreadsheetBridgeCallbackQuery(location.search),
    [location.search],
  );
  const [completeAuth] = useCompleteAssistantSpreadsheetBridgeAuthMutation();
  const [completionError, setCompletionError] = useState<string | null>(null);

  useEffect(() => {
    if (!callbackQuery.isBridgeCallback) {
      return;
    }
    if (callbackQuery.error) {
      const description = callbackQuery.errorDescription?.trim();
      setCompletionError(
        description
          ? `Google OAuth returned ${callbackQuery.error}: ${description}`
          : `Google OAuth returned ${callbackQuery.error}.`,
      );
      return;
    }
    if (!callbackQuery.state || !callbackQuery.code) {
      setCompletionError("Google OAuth callback is missing state or code.");
      return;
    }
    const oauthState = callbackQuery.state;
    const oauthCode = callbackQuery.code;

    let cancelled = false;
    const finalizeBridgeAuth = async () => {
      try {
        await completeAuth({
          state: oauthState,
          code: oauthCode,
        }).unwrap();
        if (cancelled) {
          return;
        }
        const returnContext = getAssistantSpreadsheetBridgeReturnContext();
        const safeReturnPath = isSafeInternalPath(callbackQuery.returnTo)
          ? callbackQuery.returnTo
          : ASSISTANT_SPREADSHEET_BRIDGE_SETTINGS_ROUTE;
        const targetRoute = returnContext?.originRoute || safeReturnPath;
        if (
          returnContext &&
          targetRoute &&
          targetRoute !== ASSISTANT_SPREADSHEET_BRIDGE_SETTINGS_ROUTE
        ) {
          storeAssistantSpreadsheetBridgeCompletionContext(returnContext);
        }
        clearAssistantSpreadsheetBridgeReturnContext();
        navigate(targetRoute, { replace: true });
      } catch (error) {
        if (cancelled) {
          return;
        }
        const message = (error as { message?: unknown })?.message;
        setCompletionError(
          typeof message === "string" && message.trim().length > 0
            ? message
            : "Unable to complete Google OAuth for spreadsheet export.",
        );
      }
    };

    void finalizeBridgeAuth();
    return () => {
      cancelled = true;
    };
  }, [callbackQuery, completeAuth, navigate]);

  if (completionError) {
    return (
      <AuthTransitionScreen
        testId="assistant-spreadsheet-bridge-auth-error-screen"
        title="Unable to connect Google Sheets"
        description={completionError}
      />
    );
  }

  return (
    <AuthTransitionScreen
      testId="assistant-spreadsheet-bridge-auth-completing-screen"
      title="Completing Google Sheets connection"
      description="Finishing the assistant spreadsheet bridge and returning you to the workspace."
    />
  );
};

type AuthRedirectProps = {
  returnTo: string | null;
  defaultModule: string | null;
  pendingPersona: "parent" | "staff" | "administrator" | null;
};

const AuthRedirect = ({
  returnTo,
  defaultModule,
  pendingPersona,
}: AuthRedirectProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { effectivePersona, profile } = useShellProfile();
  const shellState = useAppSelector((state) => state.shell);

  useEffect(() => {
    const resolvedPersona =
      pendingPersona && profile?.isGoogleProvider && profile?.isIntellioUser
        ? pendingPersona
        : effectivePersona;
    if (resolvedPersona !== effectivePersona) {
      dispatch(setPersonaOverride(resolvedPersona));
    }
    if (isModuleId(defaultModule)) {
      dispatch(setDefaultModule(defaultModule));
    }
    const target = resolvePostAuthTarget({
      persona: resolvedPersona,
      lastVisitedPath: shellState.lastVisitedPath,
      defaultModule: isModuleId(defaultModule)
        ? defaultModule
        : shellState.defaultModule,
      returnTo,
      authGroups: profile?.groups,
    });
    navigate(target, { replace: true });
  }, [
    defaultModule,
    dispatch,
    effectivePersona,
    navigate,
    pendingPersona,
    profile?.groups,
    profile?.isGoogleProvider,
    profile?.isIntellioUser,
    returnTo,
    shellState.defaultModule,
    shellState.lastVisitedPath,
  ]);

  return null;
};

export const AuthRoute = () => {
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const {
    effectivePersona,
    profile,
    personaProvisioningStatus,
    staffAccessPendingApproval,
    personaTokenReady,
  } = useShellProfile();
  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const bridgeCallbackQuery = useMemo(
    () => readAssistantSpreadsheetBridgeCallbackQuery(location.search),
    [location.search],
  );
  const pendingOauthStateRef = useRef(consumeOAuthRedirectState());

  const modeParam = params.get("mode");
  const isStaffActivationMode = modeParam === "activate";
  const mode: PublicAuthMode =
    modeParam === "signup" || isStaffActivationMode ? "signup" : "signin";
  const initialAccountType =
    normalizeAccountType(params.get("accountType")) ??
    (isStaffActivationMode ? "staff" : null);
  const initialEmail = normalizeQueryText(params.get("email"));
  const inviteSchoolId = normalizeQueryText(params.get("schoolId"));
  const returnToParam = params.get("returnTo");
  const defaultModuleParam = params.get("defaultModule");
  const pendingOauthState = pendingOauthStateRef.current;
  const pendingReturnTo = pendingOauthState?.returnTo ?? null;
  const pendingDefaultModule = pendingOauthState?.defaultModule ?? null;
  const personaSelectionMode = resolvePostAuthPersonaSelectionMode({
    isGoogleProvider: profile?.isGoogleProvider,
    isIntellioUser: profile?.isIntellioUser,
    groups: profile?.groups,
  });
  const personaSelectionAllowed = personaSelectionMode !== "disabled";
  const postAuthSelectionRequested = Boolean(
    pendingOauthState?.postAuthPersonaSelection && personaSelectionAllowed,
  );
  const postAuthSchoolId =
    inviteSchoolId ?? pendingOauthState?.schoolId ?? null;
  const returnTo = isSafeInternalPath(returnToParam)
    ? returnToParam
    : isSafeInternalPath(pendingReturnTo)
      ? pendingReturnTo
      : null;
  const parentLaneGoogleProvisioningIntent =
    !isStaffActivationMode &&
    mode === "signin" &&
    (initialAccountType === "parent" || isParentLaneReturnTo(returnTo))
      ? {
          email: initialEmail,
          requestedPersona: "parent" as const,
          requestedAccountType: "parent" as const,
          schoolId: inviteSchoolId,
          financialAidInvite: isFinancialAidInviteReturnTo(returnTo),
        }
      : null;
  const defaultModule = isModuleId(defaultModuleParam)
    ? defaultModuleParam
    : isModuleId(pendingDefaultModule)
      ? pendingDefaultModule
      : null;
  const pendingOauthPersona =
    !pendingOauthState?.postAuthPersonaSelection &&
    isOverridePersona(pendingOauthState?.persona)
      ? pendingOauthState.persona
      : null;
  const logoutTarget = resolveLogoutTransitionTarget();

  const handleModeChange = (nextMode: PublicAuthMode) => {
    const nextParams = new URLSearchParams(location.search);
    nextParams.set("mode", nextMode);
    if (
      isStaffActivationMode &&
      initialAccountType === "staff" &&
      !normalizeAccountType(nextParams.get("accountType"))
    ) {
      nextParams.set("accountType", "staff");
    }
    navigate(`/auth?${nextParams.toString()}`, { replace: true });
  };

  const handleAuthSuccess = () => {
    if (bridgeCallbackQuery.isBridgeCallback) {
      navigate(`${location.pathname}${location.search}`, { replace: true });
      return;
    }
    if (pendingOauthPersona) {
      dispatch(setPersonaOverride(pendingOauthPersona));
    }
    if (isModuleId(defaultModule)) {
      dispatch(setDefaultModule(defaultModule));
    }
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("ystnj-auth-session-updated"));
      const nextTarget = returnTo
        ? returnTo
        : isModuleId(defaultModule)
          ? `/?defaultModule=${encodeURIComponent(defaultModule)}`
          : "/";
      window.location.assign(nextTarget);
    }
  };

  const renderState = resolveAuthRouteRenderState({
    authStatus,
    logoutTarget,
    personaProvisioningStatus,
    effectivePersona,
    personaTokenReady,
    isBridgeCallback: bridgeCallbackQuery.isBridgeCallback,
    postAuthPersonaSelection: postAuthSelectionRequested,
    personaSelectionAllowed,
  });

  if (authStatus === "authenticated" && staffAccessPendingApproval) {
    return (
      <AuthFailureRouteScreen
        testId="auth-route-staff-pending-screen"
        title="Staff access pending"
      />
    );
  }

  switch (renderState) {
    case "logout_redirect":
      return <Navigate to={logoutTarget ?? "/auth"} replace />;
    case "configuring":
      return (
        <AuthTransitionScreen
          testId="auth-route-configuring-screen"
          title="Preparing sign in"
          description="Waiting for authentication state to settle."
        />
      );
    case "failure":
      return (
        <AuthFailureRouteScreen
          testId="auth-route-failure-screen"
          title={
            bridgeCallbackQuery.isBridgeCallback
              ? "Unable to refresh access"
              : "Unable to finish sign in"
          }
        />
      );
    case "persona_selection":
      return (
        <PostGooglePersonaSelectionScreen
          returnTo={returnTo}
          defaultModule={defaultModule}
          schoolId={postAuthSchoolId}
          selectionMode={personaSelectionMode}
        />
      );
    case "persona_provisioning":
      return (
        <AuthTransitionScreen
          testId="auth-route-persona-provisioning-screen"
          title="Preparing access"
          description="Finishing persona provisioning for this session."
        />
      );
    case "token_refresh":
      return (
        <AuthTransitionScreen
          testId="auth-route-token-refresh-screen"
          title={
            bridgeCallbackQuery.isBridgeCallback
              ? "Completing Google Sheets connection"
              : "Refreshing access"
          }
          description={
            bridgeCallbackQuery.isBridgeCallback
              ? "Waiting for role claims required to finish the spreadsheet bridge."
              : "Waiting for role claims to become available."
          }
        />
      );
    case "assistant_bridge":
      return <AssistantSpreadsheetBridgeCallback />;
    case "redirect":
      return (
        <AuthRedirect
          returnTo={returnTo}
          defaultModule={defaultModule}
          pendingPersona={pendingOauthPersona}
        />
      );
    case "public_auth":
    default:
      return (
        <PublicAuthPage
          mode={mode}
          onModeChange={handleModeChange}
          onAuthSuccess={handleAuthSuccess}
          enableGoogle={ENABLE_GOOGLE_SOCIAL}
          initialAccountType={initialAccountType}
          initialEmail={initialEmail}
          inviteSchoolId={inviteSchoolId}
          googleProvisioningIntent={parentLaneGoogleProvisioningIntent}
          oauthRedirectState={{
            returnTo,
            defaultModule,
            mode,
            schoolId: inviteSchoolId,
          }}
        />
      );
  }
};

export const AuthGate = ({ children }: { children: ReactNode }) => {
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const location = useLocation();
  const {
    effectivePersona,
    profile,
    personaProvisioningStatus,
    staffAccessPendingApproval,
    personaTokenReady,
  } = useShellProfile();
  const logoutTarget = resolveLogoutTransitionTarget();
  const personaSelectionMode = resolvePostAuthPersonaSelectionMode({
    isGoogleProvider: profile?.isGoogleProvider,
    isIntellioUser: profile?.isIntellioUser,
    groups: profile?.groups,
  });

  const renderState = resolveAuthGateRenderState({
    authStatus,
    logoutTarget,
    personaProvisioningStatus,
    effectivePersona,
    personaTokenReady,
    personaSelectionAllowed: personaSelectionMode !== "disabled",
  });

  if (authStatus === "authenticated" && staffAccessPendingApproval) {
    return <AuthFailureRouteScreen testId="auth-gate-staff-pending-screen" />;
  }

  switch (renderState) {
    case "logout_redirect":
      return <Navigate to={logoutTarget ?? "/auth"} replace />;
    case "configuring":
      return (
        <AuthTransitionScreen
          testId="auth-gate-configuring-screen"
          title="Preparing access"
          description="Waiting for authentication state to settle."
        />
      );
    case "redirect_to_auth": {
      if (isPublicFinancialAidEntryPath(location.pathname)) {
        return <PublicFinancialAidEntryRoute />;
      }
      const returnTo = encodeURIComponent(
        `${location.pathname}${location.search}`,
      );
      return <Navigate to={`/auth?returnTo=${returnTo}`} replace />;
    }
    case "failure":
      return <AuthFailureRouteScreen testId="auth-gate-failure-screen" />;
    case "persona_selection":
      return (
        <PostGooglePersonaSelectionScreen
          testId="auth-gate-post-google-persona-selection-screen"
          returnTo={`${location.pathname}${location.search}`}
          defaultModule={null}
          schoolId={null}
          selectionMode={personaSelectionMode}
        />
      );
    case "persona_provisioning":
      return (
        <AuthTransitionScreen
          testId="auth-gate-persona-provisioning-screen"
          title="Preparing access"
          description="Finishing persona provisioning for this session."
        />
      );
    case "token_refresh":
      return (
        <AuthTransitionScreen
          testId="auth-gate-token-refresh-screen"
          title="Refreshing access"
          description="Waiting for role claims to become available."
        />
      );
    case "children":
    default:
      return (
        <WorkspaceTransitionGate
          runtimePersona={effectivePersona}
          actorId={profile?.userSub ?? null}
        >
          {children}
        </WorkspaceTransitionGate>
      );
  }
};
